import { useState } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, RotateCcw, SlidersHorizontal } from 'lucide-react'
import { useAnchoredPopover } from '../hooks/useAnchoredPopover'
import { type BoxTruckCapacityLimits, type BoxTruckDatSubtypeGroupId, boxTruckDatSubtypeGroupsForLabels } from '@numeo/types-core'
import { Checkbox, InputGroup } from '@/components/ds2'
import type { BoxTruckCapacityInteraction } from '../types/filterInteractions'

interface CapacityInputProps {
    label: string
    value: number | null
    suggestion: number
    unit: string
    ariaLabel: string
    onCommit: (value: number | null) => void
}

const CapacityInput = ({ label, value, suggestion, unit, ariaLabel, onCommit }: CapacityInputProps) => {
    const [draft, setDraft] = useState(value == null ? '' : String(value))
    const commit = () => {
        if (draft.trim() === '') {
            if (value != null) onCommit(null)
            return
        }
        const parsed = Number(draft)
        if (!Number.isFinite(parsed) || parsed <= 0) {
            setDraft(value == null ? '' : String(value))
            return
        }
        if (parsed === value) return
        onCommit(parsed)
    }

    return (
        <label className="flex flex-col gap-1 text-[10px] text-foreground-muted">
            <span>{label}</span>
            <InputGroup
                inputType="number"
                variant="secondary"
                min={1}
                value={draft}
                placeholder={String(suggestion)}
                suffix={unit}
                aria-label={ariaLabel}
                onChange={(event) => setDraft(event.target.value)}
                onBlur={commit}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') event.currentTarget.blur()
                }}
            />
        </label>
    )
}

/** Weight and size controls inside the existing Box Truck loadboard. */
export const BoxTruckCapacityControls = ({
    equipmentLabels,
    limits,
    includeUnknown,
    onLimitsChange,
    onIncludeUnknownChange,
    onReset,
}: {
    equipmentLabels: string[]
    limits: BoxTruckCapacityLimits
    includeUnknown: boolean
    onLimitsChange: (limits: BoxTruckCapacityLimits, interaction: BoxTruckCapacityInteraction) => void
    onIncludeUnknownChange: (include: boolean) => void
    onReset: () => void
}) => {
    const popover = useAnchoredPopover({ panelWidth: 500 })
    const activeGroups = boxTruckDatSubtypeGroupsForLabels(equipmentLabels)
    const updateLimit = (groupId: BoxTruckDatSubtypeGroupId, field: BoxTruckCapacityInteraction['field'], value: number | null) => {
        const patch = field === 'weight' ? { weightLbs: value } : { lengthFt: value }
        onLimitsChange({ ...limits, [groupId]: { ...limits[groupId], ...patch } }, { groupId, field })
    }

    return (
        <div ref={popover.containerRef} className="inline-flex flex-wrap items-center gap-2">
            <button
                type="button"
                ref={popover.triggerRef}
                onClick={popover.togglePanel}
                aria-expanded={popover.open}
                className="flex h-8 items-center gap-1.5 rounded-lg bg-surface px-2.5 text-xs font-medium text-foreground shadow-field hover:bg-default"
            >
                <SlidersHorizontal size={12} /> Weight &amp; size <ChevronDown size={12} />
            </button>
            {popover.open &&
                popover.anchorRect &&
                createPortal(
                    <div
                        ref={popover.panelRef}
                        role="dialog"
                        aria-label="Weight and size limits"
                        style={{ ...popover.panelPositionStyle, maxHeight: window.innerHeight - 24 }}
                        className="flex flex-col overflow-hidden rounded-2xl border border-border bg-surface text-foreground shadow-overlay"
                    >
                        <div className="flex items-start justify-between gap-3 border-b border-border px-3 py-2.5">
                            <div>
                                <h3 className="text-sm font-semibold">Weight &amp; size limits</h3>
                                <p className="mt-0.5 text-[10px] text-foreground-muted">Set only the limits you want to apply.</p>
                            </div>
                            <button
                                type="button"
                                onClick={onReset}
                                className="flex h-8 items-center gap-1.5 rounded-3xl px-3 text-sm font-medium text-foreground-muted hover:bg-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                            >
                                <RotateCcw className="size-4" /> Reset
                            </button>
                        </div>

                        <div className="min-h-0 overflow-y-auto px-3" style={{ maxHeight: Math.max(140, window.innerHeight - 260) }}>
                            {activeGroups.map((group) => {
                                const groupCaps = limits[group.id]
                                return (
                                    <section key={group.id} className="border-b border-border py-2.5 last:border-0">
                                        <div className="mb-1.5 flex items-baseline justify-between gap-3">
                                            <h4 className="text-xs font-semibold">{group.title}</h4>
                                            <p className="shrink-0 text-[10px] text-foreground-muted">
                                                Suggested {group.weightLbs.toLocaleString()} lb · {group.lengthFt} ft
                                            </p>
                                        </div>
                                        {group.capacityNote && <p className="mb-1.5 text-[10px] text-foreground-muted">{group.capacityNote}</p>}
                                        <div className="grid grid-cols-2 gap-2">
                                            <CapacityInput
                                                key={`${group.id}-weight-${groupCaps.weightLbs}`}
                                                label="Max weight"
                                                value={groupCaps.weightLbs}
                                                suggestion={group.weightLbs}
                                                unit="lb"
                                                ariaLabel={`${group.title} max weight`}
                                                onCommit={(weightLbs) => updateLimit(group.id, 'weight', weightLbs)}
                                            />
                                            <CapacityInput
                                                key={`${group.id}-length-${groupCaps.lengthFt}`}
                                                label="Max length"
                                                value={groupCaps.lengthFt}
                                                suggestion={group.lengthFt}
                                                unit="ft"
                                                ariaLabel={`${group.title} max length`}
                                                onCommit={(lengthFt) => updateLimit(group.id, 'length', lengthFt)}
                                            />
                                        </div>
                                        <p className="mt-1.5 text-[10px] text-foreground-muted">Sylectus: {group.sylectusLabels.join(', ')}</p>
                                    </section>
                                )
                            })}
                            <div className="py-2.5">
                                <Checkbox checked={includeUnknown} onChange={onIncludeUnknownChange} title="Include loads missing weight or length" />
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
        </div>
    )
}
